import { Placement } from '@popperjs/core'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { usePopper } from 'react-popper'
import { SPopperModal } from './Modal.styled'

export interface Render {
  (params: { close: () => void }): ReactNode
}

interface Config {
  placement?: Placement
  offset?: number
  keepOpen?: boolean
  noStyle?: boolean
}

export const usePopperModal = (render: Render, config?: Config) => {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>()
  const [popperElement, setPopperElement] = useState<HTMLElement | null>()
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: config?.placement || 'auto',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, config?.offset ?? 4],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
        },
      },
    ],
  })

  const open = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const clickedOnPopperOrIcon =
        (popperElement && popperElement.contains(e.target as Node)) ||
        (referenceElement && referenceElement.contains(e.target as Node))
      if (!clickedOnPopperOrIcon) close()
    }

    if (!config?.keepOpen) {
      document.addEventListener('click', handleClick)
    }

    return () => {
      if (!config?.keepOpen) {
        document.removeEventListener('click', handleClick)
      }
    }
  }, [close, config?.keepOpen, popperElement, referenceElement])

  const modal = isOpen ? (
    <SPopperModal
      noStyle={config?.noStyle}
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
    >
      {render({
        close,
      })}
    </SPopperModal>
  ) : null

  return { modal, setReferenceElement, open, close }
}
